/**
 * @fileoverview Enhanced authentication context supporting Dynamic.xyz for wallet and social auth
 * @module lib/auth
 */

/* eslint-disable react-refresh/only-export-components */

import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAccount, useDisconnect } from 'wagmi';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useDynamicContext, useIsLoggedIn, useUserWallets, useSocialAccounts } from '@dynamic-labs/sdk-react-core';
import { ProviderEnum } from '@dynamic-labs/sdk-api-core';
import type { User as SupabaseAuthUser, Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { validateSessionAge, type SessionValidationResult } from './session-validator';
import type { AuthContextInterface } from '@/features/auth/types/auth';
import type { AuthProvider, UserProfile } from '@/features/auth/schemas/authSchemas';
import { UserProfileSchema } from '@/features/auth/schemas/authSchemas';

/**
 * Legacy AuthContext interface for backwards compatibility with TanStack Router.
 * This maintains the existing wallet-only authentication interface.
 *
 * @deprecated Use AuthContextInterface for new implementations
 */
export interface AuthContext {
  /** Whether a wallet is currently connected */
  isConnected: boolean;
  /** The connected wallet address (if any) */
  address?: string;
  /** Function to disconnect the current wallet */
  disconnect: () => void;
}

/**
 * Enhanced authentication context that supports both Web3 and social authentication.
 */
const AuthContextProvider = createContext<AuthContextInterface | null>(null);

/**
 * Props for the AuthProvider component.
 */
interface AuthProviderProps {
  /** Child components to be wrapped with authentication context */
  children: ReactNode;
}

/**
 * Syncs Dynamic.xyz user data to Supabase profile.
 * Creates or updates the user profile with Dynamic user info and wallet addresses.
 *
 * @param dynamicUserId - Dynamic.xyz user ID
 * @param email - User's email from Dynamic
 * @param wallets - User's connected wallets from Dynamic
 * @param verifiedCredentials - User's verified credentials (social accounts)
 */
/**
 * Verified credential structure from Dynamic.xyz SDK.
 */
interface DynamicCredential {
  format: string;
  oauthUsername?: string;
  oauthProvider?: string;
  publicIdentifier?: string;
  oauthDisplayName?: string | null;
  oauthAccountId?: string | null;
  oauthMetadata?: Record<string, unknown>;
}

/**
 * Syncs Dynamic.xyz user data to Supabase with exponential backoff retry.
 * Handles network errors gracefully on mobile where connections can be unreliable.
 *
 * @param dynamicUserId - Dynamic.xyz user ID
 * @param email - User's email from Dynamic
 * @param wallets - User's connected wallets from Dynamic
 * @param verifiedCredentials - User's verified credentials (social accounts)
 * @param maxRetries - Maximum number of retry attempts
 */
async function syncWithRetry(
  dynamicUserId: string,
  email: string | undefined,
  wallets: Array<{ address: string }>,
  verifiedCredentials: DynamicCredential[],
  maxRetries = 3
): Promise<void> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      await syncDynamicUserToSupabase(dynamicUserId, email, wallets, verifiedCredentials);
      return;
    } catch (error) {
      if (attempt === maxRetries - 1) {
        throw error;
      }
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

async function syncDynamicUserToSupabase(
  dynamicUserId: string,
  email: string | undefined,
  wallets: Array<{ address: string }>,
  verifiedCredentials: DynamicCredential[]
): Promise<void> {

  try {
    // Extract Twitter handle if available
    // Dynamic.xyz uses ProviderEnum.Twitter = "twitter"
    // Use filter + last element to get the most recently linked Twitter credential
    // (in case user has multiple or changed their linked account)
    const twitterCredentials = verifiedCredentials.filter(cred => {
      const provider = String(cred.oauthProvider || '').toLowerCase();
      return provider === 'twitter';
    });
    const twitterCredential = twitterCredentials.length > 0
      ? twitterCredentials[twitterCredentials.length - 1]
      : null;

    // Try multiple fields for Twitter handle:
    // 1. oauthUsername - primary field for OAuth username
    // 2. publicIdentifier - human-friendly identifier (could be @handle or just handle)
    // 3. oauthDisplayName - display name (might include handle)
    const extractedTwitterHandle = twitterCredential?.oauthUsername ||
      twitterCredential?.publicIdentifier ||
      twitterCredential?.oauthDisplayName;

    // Clean the handle (remove @ prefix if present)
    const twitterHandle = extractedTwitterHandle?.replace(/^@/, '') || undefined;

    // Extract Google email if available
    const googleCredential = verifiedCredentials.find(
      cred => cred.oauthProvider === 'google'
    );
    const googleEmail = googleCredential?.oauthUsername || email;
    const effectiveEmail = googleEmail || email;

    // Get primary wallet address
    const primaryWallet = wallets[0]?.address;

    // Use atomic upsert to prevent race conditions (409 Conflict errors)
    // This handles: new users, returning users, and concurrent requests
    const { error: upsertError } = await supabase
      .from('profiles')
      .upsert(
        {
          dynamic_user_id: dynamicUserId,
          email: effectiveEmail,
          twitter_handle: twitterHandle,
          wallet_address: primaryWallet,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'dynamic_user_id',
          ignoreDuplicates: false, // Update existing records
        }
      );

    // If upsert failed due to email conflict (existing user with different dynamic_user_id),
    // try to update by email to link the new Dynamic user ID
    if (upsertError) {
      // Check if there's an existing profile by email that needs its dynamic_user_id updated
      const { data: existingByEmail } = await supabase
        .from('profiles')
        .select('id, dynamic_user_id')
        .eq('email', effectiveEmail || '')
        .maybeSingle();

      if (existingByEmail && !existingByEmail.dynamic_user_id) {
        // Profile found by email without dynamic_user_id - link it
        await supabase
          .from('profiles')
          .update({
            dynamic_user_id: dynamicUserId,
            twitter_handle: twitterHandle,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingByEmail.id);
      }
      // If profile already has a different dynamic_user_id, don't overwrite it
      // This prevents hijacking another user's profile
    }

    // Sync wallet addresses to user_wallets table
    // This is optional tracking - Dynamic.xyz is the source of truth for wallets
    // Wrapped in try-catch to not break auth flow if this fails
    if (primaryWallet) {
      try {
        // First check if wallet already exists to avoid upsert issues
        const { data: existingWallet } = await supabase
          .from('user_wallets')
          .select('id')
          .eq('wallet_address', primaryWallet.toLowerCase())
          .maybeSingle();

        // Only insert if wallet doesn't exist
        if (!existingWallet) {
          await supabase
            .from('user_wallets')
            .insert({
              wallet_address: primaryWallet.toLowerCase(),
              wallet_type: 'evm',
              is_primary: true,
              // user_id left NULL - Dynamic.xyz manages the wallet association
            });
        }
      } catch {
        // Wallet sync is optional - don't break auth flow if it fails
        // Dynamic.xyz is the source of truth for wallet management
      }
    }

  } catch (error) {
    console.error('[Auth] Failed to sync Dynamic user to Supabase:', error);
    throw error;
  }
}

/**
 * Enhanced authentication provider component.
 *
 * Integrates Dynamic.xyz for wallet and social authentication,
 * and syncs user data to Supabase for profile management.
 *
 * @component
 */
export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  // Wagmi hooks for wallet state
  const { address, isConnected } = useAccount();
  const { disconnect: disconnectWallet } = useDisconnect();

  // Dynamic.xyz hooks
  const { user: dynamicUser, handleLogOut, primaryWallet } = useDynamicContext();
  const isDynamicLoggedIn = useIsLoggedIn();
  const userWallets = useUserWallets();
  const { linkSocialAccount, isLinked: isSocialLinked } = useSocialAccounts();

  // Local state
  // Note: loading starts as true but is set to false immediately once auth state is determined
  // This allows the UI to render while background sync happens
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track if we've set initial loading state
  const initialLoadCompleteRef = useRef(false);

  // Track if we've synced the current Dynamic user
  const syncedDynamicUserRef = useRef<string | null>(null);
  // Track credentials count to detect new social account links
  const syncedCredentialsCountRef = useRef<number>(0);
  // Track last sync time to prevent rapid syncs on mobile app switching
  const lastSyncTimeRef = useRef<number>(0);
  // Sync cooldown period in milliseconds (prevents sync loop on mobile)
  const SYNC_COOLDOWN_MS = 5000;

  // Fetch user profile from database using Dynamic user ID, with email fallback
  const { data: profile, isLoading: profileLoading, refetch: refetchProfile } = useQuery({
    queryKey: ['userProfile', dynamicUser?.userId, dynamicUser?.email],
    queryFn: async () => {
      if (!dynamicUser?.userId) return null;

      try {
        // First try to find by dynamic_user_id
        const { data: byDynamicId } = await supabase
          .from('profiles')
          .select('*')
          .eq('dynamic_user_id', dynamicUser.userId)
          .maybeSingle();

        if (byDynamicId) {
          try {
            return UserProfileSchema.parse(byDynamicId);
          } catch {
            return byDynamicId as UserProfile;
          }
        }

        // Fallback: try to find by email (handles Dynamic user ID changes)
        if (dynamicUser.email) {
          const { data: byEmail } = await supabase
            .from('profiles')
            .select('*')
            .eq('email', dynamicUser.email)
            .maybeSingle();

          if (byEmail) {
            try {
              return UserProfileSchema.parse(byEmail);
            } catch {
              return byEmail as UserProfile;
            }
          }
        }

        return null;
      } catch {
        return null;
      }
    },
    enabled: !!dynamicUser?.userId,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Set loading to false once we know the auth state (don't wait for Supabase sync)
  // This is a separate effect to ensure fast initial render
  useEffect(() => {
    
    // Once Dynamic has initialized (either logged in or not), we know the auth state
    // Don't wait for Supabase sync - let it happen in the background
    if (!initialLoadCompleteRef.current) {
      // Small delay to ensure Dynamic SDK has initialized
      // This prevents flash of unauthenticated content for returning users

      const timer = setTimeout(() => {
        initialLoadCompleteRef.current = true;
        setLoading(false);
              }, 100);
      return () => clearTimeout(timer);
    }
  }, [isDynamicLoggedIn]);

  // Sync Dynamic user to Supabase when auth state changes or credentials change
  // This runs in the background WITHOUT blocking the UI
  useEffect(() => {
    const credentialsCount = dynamicUser?.verifiedCredentials?.length || 0;
    const isNewUser = syncedDynamicUserRef.current !== dynamicUser?.userId;
    const hasNewCredentials = credentialsCount > syncedCredentialsCountRef.current;

    // Check cooldown to prevent rapid syncs on mobile app switching
    const now = Date.now();
    const isWithinCooldown = now - lastSyncTimeRef.current < SYNC_COOLDOWN_MS;

    if (isDynamicLoggedIn && dynamicUser?.userId && (isNewUser || hasNewCredentials)) {
      // Skip sync if within cooldown and not a new user
      if (isWithinCooldown && !isNewUser) {
        return;
      }

      syncedDynamicUserRef.current = dynamicUser.userId;
      syncedCredentialsCountRef.current = credentialsCount;
      lastSyncTimeRef.current = now;

      // Extract wallet addresses from userWallets
      const walletAddresses = userWallets.map(w => ({ address: w.address }));

      // Get verified credentials from Dynamic user - include all OAuth-related fields
      const verifiedCredentials: DynamicCredential[] = dynamicUser.verifiedCredentials?.map(cred => ({
        format: cred.format || '',
        oauthUsername: cred.oauthUsername,
        oauthProvider: cred.oauthProvider as string | undefined,
        publicIdentifier: cred.publicIdentifier,
        oauthDisplayName: cred.oauthDisplayName,
        oauthAccountId: cred.oauthAccountId,
        oauthMetadata: cred.oauthMetadata as Record<string, unknown> | undefined,
      })) || [];

      // Run sync in background - don't block UI rendering
      // Profile will be refetched after sync completes
      syncWithRetry(
        dynamicUser.userId,
        dynamicUser.email,
        walletAddresses,
        verifiedCredentials
      ).then(() => {
        // Refetch profile after sync to get any new data
        refetchProfile();
      }).catch(() => {
        // Sync failed, but UI should continue working
        // Profile fetch via useQuery will still work if profile exists
      });
    } else if (!isDynamicLoggedIn) {
      syncedDynamicUserRef.current = null;
      syncedCredentialsCountRef.current = 0;
    }
  }, [isDynamicLoggedIn, dynamicUser, userWallets, refetchProfile]);

  // Production-ready sign out
  // Note: Cleanup (cache clear, redirect) is handled by onLogout callback in providers.tsx
  // This ensures cleanup happens for both explicit logout AND token expiry
  const signOutMutation = useMutation({
    mutationFn: async () => {
      // Clear local React state first
      setSession(null);
      syncedDynamicUserRef.current = null;
      syncedCredentialsCountRef.current = 0;
      setError(null);

      // Disconnect wagmi wallet if connected
      if (isConnected) {
        try {
          disconnectWallet();
        } catch {
          // Ignore wallet disconnect errors
        }
      }

      // Log out from Dynamic - this triggers onLogout callback in providers.tsx
      // which handles cache clearing and redirect
      await handleLogOut();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Determine authentication state
  const isAuthenticated = isDynamicLoggedIn || isConnected;

  // Determine auth provider by finding the most recently added credential.
  // Dynamic.xyz orders verifiedCredentials with the newest last, so we iterate
  // in reverse to return the provider of the most recently linked credential.
  // If no social credential is found, we fall back to 'wallet'.
  const getAuthProvider = useCallback((): AuthProvider | null => {
    if (!isAuthenticated) return null;

    const credentials = dynamicUser?.verifiedCredentials;
    if (credentials && credentials.length > 0) {
      // Iterate in reverse order (newest credential last) to find the most recent social provider
      for (let i = credentials.length - 1; i >= 0; i--) {
        const provider = String(credentials[i]?.oauthProvider || '').toLowerCase();
        if (provider === 'google') return 'google';
        if (provider === 'twitter') return 'twitter';
      }
    }

    if (isConnected) return 'wallet';
    return null;
  }, [isAuthenticated, dynamicUser?.verifiedCredentials, isConnected]);

  const authProvider = getAuthProvider();
  const onboardingCompleted = profile?.onboarding_completed ?? false;
  const userType = profile?.user_type ?? null;

  // Helper to get Twitter identity from Dynamic
  // Uses findLast to get the most recently linked Twitter credential
  // (in case user has multiple or changed their linked account)
  const getTwitterIdentity = useCallback(() => {
    if (!dynamicUser?.verifiedCredentials) return null;
    const twitterCredentials = dynamicUser.verifiedCredentials.filter(cred => {
      const provider = String(cred.oauthProvider || '').toLowerCase();
      return provider === 'twitter';
    });
    // Return the last (most recent) Twitter credential
    return twitterCredentials.length > 0 ? twitterCredentials[twitterCredentials.length - 1] : null;
  }, [dynamicUser?.verifiedCredentials]);

  // Helper to extract Twitter handle
  const getTwitterHandle = useCallback(() => {
    const twitterIdentity = getTwitterIdentity();

    if (twitterIdentity) {
      // Try multiple fields for Twitter handle:
      // 1. oauthUsername - primary field for OAuth username
      // 2. publicIdentifier - human-friendly identifier
      // 3. oauthDisplayName - display name
      const extracted = twitterIdentity.oauthUsername ||
        twitterIdentity.publicIdentifier ||
        twitterIdentity.oauthDisplayName;

      if (extracted) {
        // Clean the handle (remove @ prefix if present)
        return extracted.replace(/^@/, '');
      }
    }

    // Fallback to profile data
    return profile?.twitter_handle || null;
  }, [getTwitterIdentity, profile?.twitter_handle]);

  // Helper to check if user has linked Twitter identity
  const hasLinkedTwitter = useCallback(() => {
    // Check both Dynamic's isSocialLinked and verifiedCredentials
    return isSocialLinked(ProviderEnum.Twitter) || !!getTwitterIdentity();
  }, [isSocialLinked, getTwitterIdentity]);

  // Helper to get Google identity from Dynamic
  const getGoogleIdentity = useCallback(() => {
    if (!dynamicUser?.verifiedCredentials) return null;
    return dynamicUser.verifiedCredentials.find(
      cred => cred.oauthProvider === 'google'
    );
  }, [dynamicUser?.verifiedCredentials]);

  // Helper to extract email from Google identity
  const getGoogleEmail = useCallback(() => {
    const googleIdentity = getGoogleIdentity();
    return googleIdentity?.oauthUsername ||
           dynamicUser?.email ||
           profile?.email ||
           null;
  }, [getGoogleIdentity, dynamicUser?.email, profile?.email]);

  // Helper to check if user has linked Google identity
  const hasLinkedGoogle = useCallback(() => {
    return !!getGoogleIdentity();
  }, [getGoogleIdentity]);

  // Link Twitter identity using Dynamic's social account linking
  const linkTwitterIdentity = useCallback(async () => {
    // Use Dynamic's useSocialAccounts hook to link Twitter
    // This opens the Twitter OAuth flow for the current user
    await linkSocialAccount(ProviderEnum.Twitter);
  }, [linkSocialAccount]);

  /**
   * Manually refresh the session.
   * With Dynamic, this is mostly a no-op since Dynamic handles session management.
   */
  const refreshSession = useCallback(async () => {
    // Dynamic manages sessions internally
    // This is kept for API compatibility
  }, []);

  /**
   * Get current session validation status.
   */
  const getSessionValidation = useCallback((): SessionValidationResult => {
    return validateSessionAge(session);
  }, [session]);

  // Create a mock Supabase user from Dynamic data for backwards compatibility
  const mockSupabaseUser = useMemo<SupabaseAuthUser | null>(() => {
    if (!dynamicUser) return null;
    return {
      id: dynamicUser.userId,
      email: dynamicUser.email,
      app_metadata: {
        provider: authProvider,
      },
      user_metadata: {},
      aud: 'authenticated',
      created_at: '',
      identities: dynamicUser.verifiedCredentials?.map(cred => ({
        id: cred.id || '',
        identity_id: cred.id || '',
        user_id: dynamicUser.userId,
        provider: String(cred.oauthProvider) || 'wallet',
        identity_data: {
          email: cred.oauthUsername,
          user_name: cred.oauthUsername,
          preferred_username: cred.oauthUsername,
        },
        created_at: '',
        last_sign_in_at: '',
        updated_at: '',
      })) || [],
    } as unknown as SupabaseAuthUser;
  }, [dynamicUser, authProvider]);

  // Memoized signOut function to prevent re-creating on every render
  const signOut = useCallback(async () => {
    try {
      await signOutMutation.mutateAsync();
    } catch (err) {
      throw err;
    }
  }, [signOutMutation]);

  // Memoized refreshProfile function
  const refreshProfile = useCallback(async () => {
    await refetchProfile();
  }, [refetchProfile]);

  // Memoized needsOnboarding function
  const needsOnboarding = useCallback(() => {
    return isAuthenticated && !onboardingCompleted;
  }, [isAuthenticated, onboardingCompleted]);

  // Debug: Log the combined loading state
    
  // CRITICAL: Memoize auth context value to prevent re-renders on every parent render
  // This was causing Dynamic modal to flash on hover because context consumers
  // (including DynamicWidget) were re-rendering on every mouse event
  // Large dependency array is intentional — splitting into sub-contexts would require
  // refactoring all consumers. The cost of occasional re-renders is acceptable vs the
  // risk of a large refactor.
  const authContextValue = useMemo<AuthContextInterface>(() => ({
    // Wallet authentication (legacy)
    isConnected,
    address: address || primaryWallet?.address,
    disconnect: disconnectWallet,

    // Social authentication
    isAuthenticated,
    user: mockSupabaseUser,
    profile: profile || null,
    session,
    loading: loading || profileLoading,
    error,

    // Hybrid authentication state
    authProvider,
    onboardingCompleted,
    userType: userType as AuthContextInterface['userType'],

    // Authentication actions
    signOut,
    isSigningOut: signOutMutation.isPending,
    refreshProfile,
    needsOnboarding,

    // Session management
    refreshSession,
    getSessionValidation,

    // Identity linking methods
    getTwitterIdentity,
    getTwitterHandle,
    hasLinkedTwitter,
    linkTwitterIdentity,
    getGoogleIdentity,
    getGoogleEmail,
    hasLinkedGoogle,
  }), [
    isConnected,
    address,
    primaryWallet?.address,
    disconnectWallet,
    isAuthenticated,
    mockSupabaseUser,
    profile,
    session,
    loading,
    profileLoading,
    error,
    authProvider,
    onboardingCompleted,
    userType,
    signOut,
    signOutMutation.isPending,
    refreshProfile,
    needsOnboarding,
    refreshSession,
    getSessionValidation,
    getTwitterIdentity,
    getTwitterHandle,
    hasLinkedTwitter,
    linkTwitterIdentity,
    getGoogleIdentity,
    getGoogleEmail,
    hasLinkedGoogle,
  ]);

  return (
    <AuthContextProvider.Provider value={authContextValue}>
      {children}
    </AuthContextProvider.Provider>
  );
}

/**
 * Hook to access the enhanced authentication context.
 *
 * Provides access to both Web3 wallet and social authentication state,
 * along with user profile data and authentication actions.
 *
 * @returns Enhanced authentication context
 * @throws Error if used outside AuthProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, profile, signOut } = useEnhancedAuth();
 *
 *   if (!isAuthenticated) {
 *     return <LoginPrompt />;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {profile?.full_name || user?.email}</p>
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useEnhancedAuth(): AuthContextInterface {
  const context = useContext(AuthContextProvider);

  if (!context) {
    throw new Error('useEnhancedAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Legacy authentication hook for backwards compatibility.
 *
 * @deprecated Use useEnhancedAuth for new implementations
 * @returns Legacy authentication context for wallet-only authentication
 */
export function useAuth(): AuthContext {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();

  return {
    isConnected,
    address,
    disconnect,
  };
}
